import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  inject,
  signal,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddSamplePresenter } from '../../../../presenters/add-sample.presenter';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { PatientSearchResult } from '@model/patient/patient.model';
import { Site, ExamType, Region } from '@model/filter/filter-options.model';
import { SelectOption } from '@view/shared/molecules/select/options.type';
import { DropdownItem } from '@view/shared/molecules/dropdown/dropdown.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { SaveSampleByCodePayload } from '@model/patient/sample-entry.model';
import { ExamTypeService } from '@infra-services/exam/exam-type.service';
import {
  ExamTypeInfo,
  KinshipType,
  FamilyMemberData
} from '@infra-services/exam/exam-type.interface';
import {
  colombianPhoneValidator,
  emailFormatValidator
} from '@view/shared/validators/form-validators';

export interface SampleFormValue {
  idNumber: string;
  identifierType: string;
  patientNumber: string;
  givenName: string;
  middleName: string;
  familyName: string;
  maternalFamilyName: string;
  emailAddress: string;
  birthDate: string;
  administrativeSex: string;
  maritalStatus: string;
  race: string;
  telephoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  birthCountry: string;
  placerOrderNumber: string;
  caseNumberInput: string;
  specimenId: string;
  specimenType: string;
  specimenContainerId: string;
  examType: string;
  site: string;
  region: string;
  consentForUseOfSamples: boolean;
  userDataDescription: string;
  descriptionDataSample: string;
  descriptionDataExam: string;
  clinicalHistory: string;
  prioritization: boolean;
  acmg: boolean;
  mlpa: boolean;
  priorityId: string;
  isReprocess: boolean;
  causeReprocessId: string;
  statusReprocessId: string;
  reprocessDescription: string;
  laboratoriesId: string;
  diagnosesId: string;
  specialistsId: string;
  companyId: string;
  providerId: string;
}

@Component({
  template: '',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule]
})
export abstract class AddSampleBaseComponent implements OnInit, OnDestroy {
  @Input() showToast = true;
  @Input() isModal = false;
  @Output() sampleCompleted = new EventEmitter<void>();

  ButtonTypeEnum = ButtonTypeEnum;
  ButtonSizeEnum = ButtonSizeEnum;

  showForm = signal(false);
  searchValue = signal('');
  isSearching = signal(false);
  caseNumber = signal<string>('');

  searchForm: FormGroup;
  sampleForm: FormGroup;

  specimenTypes = signal<{ id: string; name: string }[]>([]);
  specimenTypeOptions = signal<SelectOption<string>[]>([]);
  specimenTypeDropdownOptions = signal<DropdownItem[]>([]);

  siteOptions = signal<SelectOption<string>[]>([]);
  siteDropdownOptions = signal<DropdownItem[]>([]);

  examTypeOptions = signal<SelectOption<string>[]>([]);
  examTypeDropdownOptions = signal<DropdownItem[]>([]);

  regionOptions = signal<SelectOption<string>[]>([]);
  regionDropdownOptions = signal<DropdownItem[]>([]);

  identifierTypeOptions: SelectOption<string>[] = [
    { label: 'CC', value: 'CC' },
    { label: 'TI', value: 'TI' },
    { label: 'CE', value: 'CE' },
    { label: 'PA', value: 'PA' }
  ];

  identifierTypeDropdownOptions: DropdownItem[] = [
    { label: 'CC', value: 'CC' },
    { label: 'TI', value: 'TI' },
    { label: 'CE', value: 'CE' },
    { label: 'PA', value: 'PA' }
  ];

  administrativeSexOptions: SelectOption<string>[] = [
    { label: 'Femenino', value: 'F' },
    { label: 'Masculino', value: 'M' }
  ];

  administrativeSexDropdownOptions: DropdownItem[] = [
    { label: 'Femenino', value: 'F' },
    { label: 'Masculino', value: 'M' }
  ];

  priorityOptions = signal<SelectOption<string>[]>([]);
  priorityDropdownOptions = signal<DropdownItem[]>([]);

  laboratoriesOptions = signal<SelectOption<string>[]>([]);
  laboratoriesDropdownOptions = signal<DropdownItem[]>([]);

  diagnosesOptions = signal<SelectOption<string>[]>([]);
  diagnosesDropdownOptions = signal<DropdownItem[]>([]);

  specialistsOptions = signal<SelectOption<string>[]>([]);
  specialistsDropdownOptions = signal<DropdownItem[]>([]);

  causeReprocessOptions = signal<SelectOption<string>[]>([]);
  causeReprocessDropdownOptions = signal<DropdownItem[]>([]);

  statusReprocessOptions = signal<SelectOption<string>[]>([]);
  statusReprocessDropdownOptions = signal<DropdownItem[]>([]);

  specimenContainerOptions = signal<SelectOption<string>[]>([]);
  specimenContainerDropdownOptions = signal<DropdownItem[]>([]);

  providerOptions = signal<SelectOption<string>[]>([]);
  providerDropdownOptions = signal<DropdownItem[]>([]);

  insuranceCompanyOptions = signal<SelectOption<string>[]>([]);
  insuranceCompanyDropdownOptions = signal<DropdownItem[]>([]);

  examTypeInfo = signal<ExamTypeInfo | null>(null);
  familyMembers = signal<FamilyMemberData[]>([]);

  protected readonly presenter = inject(AddSamplePresenter);
  protected readonly fb = inject(FormBuilder);
  protected readonly toastService = inject(ToastService);
  protected readonly examTypeService = inject(ExamTypeService);

  protected getFormControl(
    formGroup: FormGroup,
    controlName: string
  ): FormControl {
    return formGroup.get(controlName) as FormControl;
  }

  get searchIdentifierTypeControl() {
    return this.getFormControl(this.searchForm, 'identifierType');
  }

  get documentNumberControl() {
    return this.getFormControl(this.searchForm, 'documentNumber');
  }

  get specimenCodeControl() {
    return this.getFormControl(this.searchForm, 'specimenCode');
  }

  get givenNameControl() {
    return this.getFormControl(this.sampleForm, 'givenName');
  }

  get familyNameControl() {
    return this.getFormControl(this.sampleForm, 'familyName');
  }

  get identifierTypeControl() {
    return this.getFormControl(this.sampleForm, 'identifierType');
  }

  get idNumberControl() {
    return this.getFormControl(this.sampleForm, 'idNumber');
  }

  get administrativeSexControl() {
    return this.getFormControl(this.sampleForm, 'administrativeSex');
  }

  get emailAddressControl() {
    return this.getFormControl(this.sampleForm, 'emailAddress');
  }

  get cityControl() {
    return this.getFormControl(this.sampleForm, 'city');
  }

  get stateControl() {
    return this.getFormControl(this.sampleForm, 'state');
  }

  get birthDateControl() {
    return this.getFormControl(this.sampleForm, 'birthDate');
  }

  get regionControl() {
    return this.getFormControl(this.sampleForm, 'region');
  }

  get examNameControl() {
    return this.getFormControl(this.sampleForm, 'examName');
  }

  get examCodeControl() {
    return this.getFormControl(this.sampleForm, 'examCode');
  }

  get placerOrderNumberControl() {
    return this.getFormControl(this.sampleForm, 'placerOrderNumber');
  }

  get admissionDateControl() {
    return this.getFormControl(this.sampleForm, 'admissionDate');
  }

  get specimenTypeControl() {
    return this.getFormControl(this.sampleForm, 'specimenType');
  }

  get examTypeControl() {
    return this.getFormControl(this.sampleForm, 'examType');
  }

  get siteControl() {
    return this.getFormControl(this.sampleForm, 'site');
  }

  get middleNameControl() {
    return this.getFormControl(this.sampleForm, 'middleName');
  }

  get maternalFamilyNameControl() {
    return this.getFormControl(this.sampleForm, 'maternalFamilyName');
  }

  get birthCountryControl() {
    return this.getFormControl(this.sampleForm, 'birthCountry');
  }

  get priorityIdControl() {
    return this.getFormControl(this.sampleForm, 'priorityId');
  }

  get laboratoriesIdControl() {
    return this.getFormControl(this.sampleForm, 'laboratoriesId');
  }

  get diagnosesIdControl() {
    return this.getFormControl(this.sampleForm, 'diagnosesId');
  }

  get specialistsIdControl() {
    return this.getFormControl(this.sampleForm, 'specialistsId');
  }

  get causeReprocessIdControl() {
    return this.getFormControl(this.sampleForm, 'causeReprocessId');
  }

  get statusReprocessIdControl() {
    return this.getFormControl(this.sampleForm, 'statusReprocessId');
  }

  get patientNumberControl() {
    return this.getFormControl(this.sampleForm, 'patientNumber');
  }

  get maritalStatusControl() {
    return this.getFormControl(this.sampleForm, 'maritalStatus');
  }

  get raceControl() {
    return this.getFormControl(this.sampleForm, 'race');
  }

  get telephoneNumberControl() {
    return this.getFormControl(this.sampleForm, 'telephoneNumber');
  }

  get streetAddressControl() {
    return this.getFormControl(this.sampleForm, 'streetAddress');
  }

  get specimenIdControl() {
    return this.getFormControl(this.sampleForm, 'specimenId');
  }

  get specimenContainerIdControl() {
    return this.getFormControl(this.sampleForm, 'specimenContainerId');
  }

  get companyIdControl() {
    return this.getFormControl(this.sampleForm, 'companyId');
  }

  get providerIdControl() {
    return this.getFormControl(this.sampleForm, 'providerId');
  }

  get caseNumberInputControl() {
    return this.getFormControl(this.sampleForm, 'caseNumberInput');
  }

  get userDataDescriptionControl() {
    return this.getFormControl(this.sampleForm, 'userDataDescription');
  }

  get descriptionDataSampleControl() {
    return this.getFormControl(this.sampleForm, 'descriptionDataSample');
  }

  get descriptionDataExamControl() {
    return this.getFormControl(this.sampleForm, 'descriptionDataExam');
  }

  get clinicalHistoryControl() {
    return this.getFormControl(this.sampleForm, 'clinicalHistory');
  }

  isReprocessChecked(): boolean {
    return this.sampleForm.get('isReprocess')?.value === true;
  }

  protected maxDateValidator() {
    return (control: FormControl): Record<string, any> | null => {
      if (!control.value) {
        return null;
      }

      const inputDate = new Date(control.value);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (inputDate > today) {
        return {
          maxDate: {
            value: control.value,
            message: 'La fecha de nacimiento no puede ser futura'
          }
        };
      }

      return null;
    };
  }

  constructor() {
    this.searchForm = this.fb.group({
      identifierType: ['', Validators.required],
      documentNumber: ['', Validators.required],
      specimenCode: ['', Validators.required]
    });

    this.sampleForm = this.fb.group({
      identifierType: ['', Validators.required],
      idNumber: [{ value: '', disabled: true }, Validators.required],
      patientNumber: [''],
      givenName: ['', Validators.required],
      middleName: [''],
      familyName: ['', Validators.required],
      maternalFamilyName: [''],
      administrativeSex: ['', Validators.required],
      maritalStatus: [''],
      race: [''],
      telephoneNumber: ['', [colombianPhoneValidator()]],
      streetAddress: [''],
      emailAddress: ['', [emailFormatValidator()]],
      birthDate: ['', [Validators.required, this.maxDateValidator()]],
      city: [''],
      state: [''],
      birthCountry: [''],
      region: ['', Validators.required],
      specimenId: [''],
      specimenType: ['', Validators.required],
      specimenContainerId: [''],
      admissionDate: ['', Validators.required],
      examName: [{ value: '', disabled: true }],
      examCode: ['', Validators.required],
      examType: ['', Validators.required],
      placerOrderNumber: ['', Validators.required],
      caseNumberInput: [''],
      site: ['', Validators.required],
      consentForUseOfSamples: [false],
      prioritization: [false],
      acmg: [false],
      mlpa: [false],
      priorityId: [''],
      userDataDescription: [''],
      descriptionDataSample: [''],
      descriptionDataExam: [''],
      clinicalHistory: [''],
      laboratoriesId: [''],
      diagnosesId: [''],
      specialistsId: [''],
      companyId: [''],
      providerId: [''],
      isReprocess: [false],
      causeReprocessId: [''],
      statusReprocessId: [''],
      reprocessDescription: ['']
    });

    const destroyRef = inject(DestroyRef);

    this.setupUppercaseTransformation();

    this.sampleForm
      .get('isReprocess')
      ?.valueChanges.pipe(takeUntilDestroyed(destroyRef))
      .subscribe((isReprocess: boolean | null) => {
        this.updateReprocessValidations(isReprocess ?? false);
      });

    this.sampleForm
      .get('specimenId')
      ?.valueChanges.pipe(takeUntilDestroyed(destroyRef))
      .subscribe((value: string | null) => {
        const caseNumberControl = this.sampleForm.get('caseNumberInput');
        if (caseNumberControl && caseNumberControl.value !== value) {
          caseNumberControl.setValue(value ?? '', { emitEvent: false });
        }
      });
  }

  private setupUppercaseTransformation(): void {
    const destroyRef = inject(DestroyRef);
    const fieldsToUppercase = [
      'givenName',
      'middleName',
      'familyName',
      'maternalFamilyName',
      'city',
      'state',
      'streetAddress',
      'race',
      'maritalStatus',
      'userDataDescription',
      'descriptionDataSample',
      'descriptionDataExam',
      'clinicalHistory'
    ];

    fieldsToUppercase.forEach((fieldName) => {
      this.sampleForm
        .get(fieldName)
        ?.valueChanges.pipe(takeUntilDestroyed(destroyRef))
        .subscribe((value: string | null) => {
          if (
            value &&
            typeof value === 'string' &&
            value !== value.toUpperCase()
          ) {
            this.sampleForm.get(fieldName)?.setValue(value.toUpperCase(), {
              emitEvent: false
            });
          }
        });
    });
  }

  protected updateReprocessValidations(isReprocess: boolean): void {
    const causeControl = this.sampleForm.get('causeReprocessId');
    const statusControl = this.sampleForm.get('statusReprocessId');
    const descriptionControl = this.sampleForm.get('reprocessDescription');

    const givenNameControl = this.sampleForm.get('givenName');
    const familyNameControl = this.sampleForm.get('familyName');
    const birthDateControl = this.sampleForm.get('birthDate');

    if (isReprocess) {
      causeControl?.setValidators([Validators.required]);
      statusControl?.setValidators([Validators.required]);
      descriptionControl?.setValidators([Validators.required]);

      givenNameControl?.disable();
      familyNameControl?.disable();
      birthDateControl?.disable();
    } else {
      causeControl?.clearValidators();
      statusControl?.clearValidators();
      descriptionControl?.clearValidators();

      causeControl?.setValue('');
      statusControl?.setValue('');
      descriptionControl?.setValue('');

      givenNameControl?.enable();
      familyNameControl?.enable();
      birthDateControl?.enable();
    }

    causeControl?.updateValueAndValidity();
    statusControl?.updateValueAndValidity();
    descriptionControl?.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.loadSpecimenTypes();
    this.loadSites();
    this.loadExamTypes();
    this.loadRegions();
    this.loadDiagnoses();
    this.loadSpecialists();
    this.loadPriorities();
    this.loadLaboratories();
    this.loadSpecimenContainers();
    this.loadProviders();
    this.loadInsuranceCompanies();
    this.loadReprocessCauses();
    this.loadReprocessStatuses();
  }

  loadSpecimenTypes(): void {
    this.presenter.loadSpecimenTypes().subscribe({
      next: (types) => {
        this.specimenTypes.set(types);
        const options = types.map((type) => ({
          label: type.name,
          value: type.id
        }));
        this.specimenTypeOptions.set(options);
        this.specimenTypeDropdownOptions.set(options);
      },
      error: () => {
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error al cargar tipos de muestra',
          message:
            'No se pudieron cargar los tipos de muestra. Por favor, recargue la página.'
        });
      }
    });
  }

  loadSites(): void {
    this.presenter.loadSites().subscribe({
      next: (sites: Site[]) => {
        const options = sites.map((site) => ({
          label: site.name,
          value: site.id
        }));
        this.siteOptions.set(options);
        this.siteDropdownOptions.set(options);
      },
      error: () => {
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error al cargar sedes',
          message:
            'No se pudieron cargar las sedes. Por favor, recargue la página.'
        });
      }
    });
  }

  loadExamTypes(): void {
    this.presenter.loadExamTypes().subscribe({
      next: (examTypes: ExamType[]) => {
        const options = examTypes.map((examType) => ({
          label: examType.name,
          value: examType.code
        }));
        this.examTypeOptions.set(options);
        this.examTypeDropdownOptions.set(options);
      },
      error: () => {
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error al cargar tipos de examen',
          message:
            'No se pudieron cargar los tipos de examen. Por favor, recargue la página.'
        });
      }
    });
  }

  loadRegions(): void {
    this.presenter.loadRegions().subscribe({
      next: (regions: Region[]) => {
        const options = regions.map((region) => ({
          label: region.name,
          value: region.id
        }));
        this.regionOptions.set(options);
        this.regionDropdownOptions.set(options);
      },
      error: () => {
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error al cargar regiones',
          message:
            'No se pudieron cargar las regiones. Por favor, recargue la página.'
        });
      }
    });
  }

  loadDiagnoses(): void {
    this.presenter.loadDiagnoses().subscribe({
      next: (diagnoses: { id: string; name: string }[]) => {
        const options = diagnoses.map((diagnosis) => ({
          label: diagnosis.name,
          value: diagnosis.id
        }));
        this.diagnosesOptions.set(options);
        this.diagnosesDropdownOptions.set(options);
      },
      error: () => {
        this.diagnosesOptions.set([]);
        this.diagnosesDropdownOptions.set([]);
      }
    });
  }

  loadSpecialists(): void {
    this.presenter.loadSpecialists().subscribe({
      next: (specialists: { id: string; name: string }[]) => {
        const options = specialists.map((specialist) => ({
          label: specialist.name,
          value: specialist.id
        }));
        this.specialistsOptions.set(options);
        this.specialistsDropdownOptions.set(options);
      },
      error: () => {
        this.specialistsOptions.set([]);
        this.specialistsDropdownOptions.set([]);
      }
    });
  }

  loadPriorities(): void {
    this.presenter.loadPriorities().subscribe({
      next: (priorities: { id: string; name: string }[]) => {
        const options = priorities.map((priority) => ({
          label: priority.name,
          value: priority.id
        }));
        this.priorityOptions.set(options);
        this.priorityDropdownOptions.set(options);
      },
      error: () => {
        this.priorityOptions.set([]);
        this.priorityDropdownOptions.set([]);
      }
    });
  }

  loadLaboratories(): void {
    this.presenter.loadLaboratories().subscribe({
      next: (laboratories: { id: string; name: string }[]) => {
        const options = laboratories.map((laboratory) => ({
          label: laboratory.name,
          value: laboratory.id
        }));
        this.laboratoriesOptions.set(options);
        this.laboratoriesDropdownOptions.set(options);
      },
      error: () => {
        this.laboratoriesOptions.set([]);
        this.laboratoriesDropdownOptions.set([]);
      }
    });
  }

  loadReprocessCauses(): void {
    this.presenter.loadReprocessCauses().subscribe({
      next: (causes: { id: string; name: string }[]) => {
        const options = causes.map((cause) => ({
          label: cause.name,
          value: cause.id
        }));
        this.causeReprocessOptions.set(options);
        this.causeReprocessDropdownOptions.set(options);
      },
      error: () => {
        this.causeReprocessOptions.set([]);
        this.causeReprocessDropdownOptions.set([]);
      }
    });
  }

  loadReprocessStatuses(): void {
    this.presenter.loadReprocessStatuses().subscribe({
      next: (statuses: { id: string; name: string }[]) => {
        const options = statuses.map((status) => ({
          label: status.name,
          value: status.id
        }));
        this.statusReprocessOptions.set(options);
        this.statusReprocessDropdownOptions.set(options);
      },
      error: () => {
        this.statusReprocessOptions.set([]);
        this.statusReprocessDropdownOptions.set([]);
      }
    });
  }

  loadSpecimenContainers(): void {
    this.presenter.loadSpecimenContainers().subscribe({
      next: (containers: { id: string; name: string }[]) => {
        const options = containers.map((container) => ({
          label: container.name,
          value: container.id
        }));
        this.specimenContainerOptions.set(options);
        this.specimenContainerDropdownOptions.set(options);
      },
      error: () => {
        this.specimenContainerOptions.set([]);
        this.specimenContainerDropdownOptions.set([]);
      }
    });
  }

  loadProviders(): void {
    this.presenter.loadProviders().subscribe({
      next: (providers: { id: string; name: string }[]) => {
        const options = providers.map((provider) => ({
          label: provider.name,
          value: provider.id
        }));
        this.providerOptions.set(options);
        this.providerDropdownOptions.set(options);
      },
      error: () => {
        this.providerOptions.set([]);
        this.providerDropdownOptions.set([]);
      }
    });
  }

  loadInsuranceCompanies(): void {
    this.presenter.loadInsuranceCompanies().subscribe({
      next: (companies: { id: string; name: string }[]) => {
        const options = companies.map((company) => ({
          label: company.name,
          value: company.id
        }));
        this.insuranceCompanyOptions.set(options);
        this.insuranceCompanyDropdownOptions.set(options);
      },
      error: () => {
        this.insuranceCompanyOptions.set([]);
        this.insuranceCompanyDropdownOptions.set([]);
      }
    });
  }

  clearSearch(): void {
    this.showForm.set(false);
    this.searchValue.set('');
    this.caseNumber.set('');
    this.searchForm.reset();
    this.sampleForm.reset();
  }

  populateForm(data: PatientSearchResult): void {
    this.caseNumber.set(data.caseNumber ?? '');

    this.sampleForm.patchValue({
      identifierType: data.identifierTypeCode,
      idNumber: data.idNumber,
      givenName: data.givenName,
      middleName: data.middleName ?? '',
      familyName: data.familyName,
      maternalFamilyName: data.maternalFamilyName ?? '',
      administrativeSex: data.administrativeSex,
      emailAddress: data.emailAddress,
      birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
      city: data.city,
      state: data.state,
      birthCountry: data.country ?? '',
      region: data.regional?.id ?? data.region,
      specimenType: data.specimenType?.id,
      admissionDate: data.admissionDate ? data.admissionDate.split('T')[0] : '',
      examName: data.examGroup?.name ?? '',
      examCode: data.examCode,
      examType:
        data.examNameId ??
        (data.examGroup?.id ? String(data.examGroup.id) : ''),
      placerOrderNumber: data.placerOrderNumber,
      site: data.site?.id,
      priorityId: data.priority?.id ?? ''
    });

    const examId =
      data.examNameId ??
      (data.examGroup?.id ? String(data.examGroup.id) : null);
    if (examId) {
      this.checkExamTypeForFamilyMembers(examId);
    }
  }

  protected checkExamTypeForFamilyMembers(examId: string): void {
    this.examTypeService.getExamTypeInfo(examId).subscribe({
      next: (info) => {
        this.examTypeInfo.set(info);

        if (info.requiresFamilyMembers) {
          this.showFamilyMembersAlert(info);
        }
      },
      error: () => {
        this.examTypeInfo.set(null);
      }
    });
  }

  protected showFamilyMembersAlert(info: ExamTypeInfo): void {
    let message = '';
    let title = '';

    if (info.isTrio) {
      title = ' Examen TRIO Detectado';
      message =
        'Este examen requiere agregar los datos del PADRE y la MADRE (ambos obligatorios).';
    } else if (info.isDuo) {
      title = ' Examen DUO Detectado';
      message =
        'Este examen requiere agregar los datos del PADRE o la MADRE (uno de los dos obligatorio).';
    } else if (info.isFuturosPadres) {
      title = '️ Examen Futuros Padres Detectado';
      message = 'Este examen requiere agregar los datos de la pareja.';
    }

    if (message) {
      this.toastService.setToast({
        type: ToastTypeEnum.WARNING,
        title,
        message
      });
    }
  }

  requiresFamilyMembers(): boolean {
    return this.examTypeInfo()?.requiresFamilyMembers ?? false;
  }

  getExamTypeMessage(): string {
    const info = this.examTypeInfo();
    if (!info) {
      return '';
    }

    if (info.isTrio) {
      return 'Este examen es de tipo TRIO. Debe agregar los datos del PADRE y la MADRE (ambos obligatorios).';
    }
    if (info.isDuo) {
      return 'Este examen es de tipo DUO. Debe agregar los datos del PADRE o la MADRE (uno obligatorio).';
    }
    if (info.isFuturosPadres) {
      return 'Este examen es de tipo Futuros Padres. Debe agregar los datos de la pareja.';
    }
    return '';
  }

  getAvailableKinshipTypes(): KinshipType[] {
    const info = this.examTypeInfo();
    if (!info) {
      return [];
    }

    const patientSex = this.sampleForm.get('administrativeSex')?.value;
    return this.examTypeService.getRequiredKinshipTypes(info, patientSex);
  }

  hasFamilyMember(kinshipType: KinshipType): boolean {
    return this.familyMembers().some((m) => m.tipoParentesco === kinshipType);
  }

  getKinshipTypeLabel(kinshipType: KinshipType): string {
    const labels: Record<KinshipType, string> = {
      [KinshipType.PADRE]: 'Padre',
      [KinshipType.MADRE]: 'Madre',
      [KinshipType.FUTURA_MADRE]: 'Futura Madre',
      [KinshipType.FUTURO_PADRE]: 'Futuro Padre'
    };
    return labels[kinshipType] || kinshipType;
  }

  addFamilyMember(member: FamilyMemberData): void {
    const current = this.familyMembers();
    if (!this.hasFamilyMember(member.tipoParentesco)) {
      this.familyMembers.set([...current, member]);
    }
  }

  removeFamilyMember(kinshipType: KinshipType): void {
    const current = this.familyMembers();
    this.familyMembers.set(
      current.filter((m) => m.tipoParentesco !== kinshipType)
    );
  }

  canSaveWithFamilyMembers(): boolean {
    const info = this.examTypeInfo();
    if (!info?.requiresFamilyMembers) {
      return true;
    }

    if (info.isTrio) {
      return (
        this.hasFamilyMember(KinshipType.PADRE) &&
        this.hasFamilyMember(KinshipType.MADRE)
      );
    }

    if (info.isDuo) {
      return (
        this.hasFamilyMember(KinshipType.PADRE) ||
        this.hasFamilyMember(KinshipType.MADRE)
      );
    }

    if (info.isFuturosPadres) {
      return (
        this.hasFamilyMember(KinshipType.FUTURA_MADRE) ||
        this.hasFamilyMember(KinshipType.FUTURO_PADRE)
      );
    }

    return true;
  }

  cancelSample(): void {
    if (
      confirm('¿Está seguro de cancelar? Los datos no guardados se perderán.')
    ) {
      this.clearSearch();
    }
  }

  private getStringValue(value: any): string {
    return value ?? '';
  }

  protected buildSavePayload(
    formValue: SampleFormValue
  ): SaveSampleByCodePayload {
    const isReprocess = formValue.isReprocess;

    return {
      datosOrden: {
        laboratoriesId: this.getStringValue(formValue.laboratoriesId),
        diagnosesId: this.getStringValue(formValue.diagnosesId),
        specialistsId: this.getStringValue(formValue.specialistsId),
        siteId: formValue.site,
        regionalId: formValue.region,
        consentForUseOfSamples: formValue.consentForUseOfSamples,
        userDataDescription: this.getStringValue(formValue.userDataDescription),
        descriptionDataSample: this.getStringValue(
          formValue.descriptionDataSample
        ),
        descriptionDataExam: this.getStringValue(formValue.descriptionDataExam),
        clinicalHistory: this.getStringValue(formValue.clinicalHistory),
        prioritization: formValue.prioritization,
        acmg: formValue.acmg,
        mlpa: formValue.mlpa,
        priorityId: this.getStringValue(formValue.priorityId),
        providerId: this.getStringValue(formValue.providerId),
        companyId: this.getStringValue(formValue.companyId)
      },
      datosReproceso: {
        isReprocess,
        causeReprocessId: isReprocess
          ? formValue.causeReprocessId || null
          : null,
        statusReprocessId: isReprocess
          ? formValue.statusReprocessId || null
          : null,
        reprocessDescription: isReprocess
          ? formValue.reprocessDescription || null
          : null
      }
    };
  }

  protected handleSaveSuccess(): void {
    this.toastService.setToast({
      title: 'Muestra guardada exitosamente',
      type: ToastTypeEnum.SUCCESS
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
  }

  protected handleSaveError(error: any): void {
    const errorMessage = this.getSaveErrorMessage(error);
    this.toastService.setToast({
      type: ToastTypeEnum.ERROR,
      title: 'Error al guardar',
      message: errorMessage
    });
  }

  protected getSaveErrorMessage(error: any): string {
    const backendMessage = this.extractBackendMessage(error);
    if (backendMessage) {
      return backendMessage;
    }

    const errorMessages: Record<number, string> = {
      400: 'Datos inválidos. Por favor, verifique la información.',
      401: 'No autorizado. Por favor, inicie sesión nuevamente.',
      403: 'No tiene permisos para realizar esta acción.',
      500: 'Error interno del servidor. Contacte al administrador.'
    };

    return errorMessages[error.status] || 'Error al guardar la muestra';
  }

  protected extractBackendMessage(error: any): string | null {
    // Handle raw HttpErrorResponse — check error body first
    if (error?.error && typeof error.error === 'object') {
      const errorObj = error.error as Record<string, unknown>;
      if ('message' in errorObj && typeof errorObj['message'] === 'string') {
        return errorObj['message'];
      }
    }

    // Handle original error nested inside transformed error
    if (error?.originalError?.error) {
      const originalBody = error.originalError.error as Record<string, unknown>;
      if (
        'message' in originalBody &&
        typeof originalBody['message'] === 'string'
      ) {
        return originalBody['message'];
      }
    }

    if (typeof error?.error === 'string' && error.error.length > 0) {
      return error.error;
    }

    // Handle transformed error from HttpErrorHandlerService
    // Shape: { operation, message, status, originalError }
    // Checked last to avoid picking up HttpErrorResponse.message (HTTP wrapper text)
    if (
      error?.message &&
      typeof error.message === 'string' &&
      !(error instanceof HttpErrorResponse)
    ) {
      return error.message;
    }

    return null;
  }

  ngOnDestroy(): void {
    this.toastService.setToast(null);
  }
}
